import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import StudyCalendar from '../components/StudyCalendar';
import pdfExportService from '../services/pdfExportService';
import { CalendarSkeleton } from '../components/SkeletonLoader';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

const Calendar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [studyPlan, setStudyPlan] = useState(null);
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [showTips, setShowTips] = useState(false);

  useEffect(() => {
    // Check authentication
    if (!user) {
      navigate('/login');
      return;
    }

    // Load study plan from localStorage or API
    const loadStudyPlan = () => {
      try {
        const savedPlan = localStorage.getItem('studyPlan');
        if (savedPlan) {
          setStudyPlan(JSON.parse(savedPlan));
        }
      } catch (error) {
        console.error('Error loading study plan:', error);
      }
      
      // Simulate loading time
      setTimeout(() => {
        setLoading(false);
      }, 1500);
    };

    loadStudyPlan();
  }, [user, navigate]);

  const handleDateClick = (date, events) => {
    console.log('Date clicked:', date, events);
    // Handle date click - could open detailed view or add events
  };

  const handleDownloadICS = () => {
    if (studyPlan) {
      try {
        pdfExportService.generateICSFile(studyPlan);
      } catch (error) {
        console.error('Error downloading ICS file:', error);
      }
    }
  };

  const handleCreatePlan = () => {
    navigate('/planner');
  };

  // Show skeleton while loading
  if (loading) {
    return <CalendarSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="container mx-auto px-4 py-6">

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            📅 My Calendar
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Organize your study schedule and track your learning progress with smart calendar features
          </p>
        </div>

        {/* Always show the calendar */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <StudyCalendar
            studyPlan={studyPlan}
            onDateClick={handleDateClick}
            onDownloadICS={handleDownloadICS}
          />
        </motion.div>

        {/* Tips Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-16"
        >
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl border border-blue-200 dark:border-blue-700 overflow-hidden">            
            
            {/* Collapsible Content */}
            <AnimatePresence>
              {showTips && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                          <p className="text-gray-700 dark:text-gray-300">
                            Click on any date to view detailed study sessions and motivational messages
                          </p>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                          <p className="text-gray-700 dark:text-gray-300">
                            Use the "Export Calendar" button to download your schedule as an ICS file
                          </p>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                          <p className="text-gray-700 dark:text-gray-300">
                            Green highlighted dates indicate study days with scheduled sessions
                          </p>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                          <p className="text-gray-700 dark:text-gray-300">
                            The exam countdown shows days remaining until your final exam
                          </p>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                          <p className="text-gray-700 dark:text-gray-300">
                            Mark completed sessions to track your daily progress
                          </p>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                          <p className="text-gray-700 dark:text-gray-300">
                            Use the "Today" button to quickly navigate to the current date
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Calendar; 